import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DataTable from '../../components/DataTable';
import DebouncedSearchInput from '../../components/DebouncedSearchInput';
import Button from '../../components/Button';
import journalEntriesAPI from '../../api/journalEntriesAPI';
import accountingClientsAPI from '../../api/accountingClientsAPI';
import { getCachedRouteData } from '../../utils/routePrefetch';
import { formatDate, currentMonthRange } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/numberUtils';
import { useAuth } from '../../context/AuthContext';
import useCompanyContext from '../../hooks/useCompanyContext';

const JournalEntriesList = () => {
    const { selectedClient, user, isAuthReady } = useAuth();
    const { accountingClientId: contextAccountingClientId } = useCompanyContext();
    const tenantType = localStorage.getItem('tenantType') || '';
    const isBillingTenant = tenantType === 'billing' || user?.tenantType === 'billing';
    const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
    const storeId = localStorage.getItem('storeId')
        || storedUser?.store
        || storedUser?.storeId
        || storedUser?.stores?.[0]
        || '';
    const [allEntries, setAllEntries] = useState([]);
    const [filteredEntries, setFilteredEntries] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    // Default to the current month — loading every entry made this page slow.
    const [dateRange, setDateRange] = useState(() => {
        const { first, last } = currentMonthRange();
        return { startDate: first, endDate: last };
    });
    // ✅ Accounting client state for multi-tenant filtering
    const [accountingClients, setAccountingClients] = useState([]);
    const [selectedAccountingClient, setSelectedAccountingClient] = useState(null);
    const [contextLoading, setContextLoading] = useState(false);

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const activeAccountingClientId = contextAccountingClientId
        || (isBillingTenant
            ? (localStorage.getItem('accountingClientId') || localStorage.getItem('storeId') || '')
            : selectedAccountingClient)
        || '';
    const selectedClientRecord = accountingClients.find((client) => client._id === activeAccountingClientId);
    const isKaryuClient = /\bkaryu\b/i.test(String(
        selectedClientRecord?.name || localStorage.getItem('accountingClientName') || ''
    ));

    const journalEntriesQuery = useQuery({
        queryKey: ['journal-entries', activeAccountingClientId, isKaryuClient ? statusFilter : 'all', dateRange.startDate, dateRange.endDate],
        enabled: isAuthReady && !!activeAccountingClientId,
        queryFn: async () => {
            const params = { populate: 'createdBy,postedBy', limit: 10000 };
            if (isKaryuClient) params.statusFilter = statusFilter;
            if (dateRange.startDate) params.startDate = dateRange.startDate;
            if (dateRange.endDate) params.endDate = dateRange.endDate;
            const response = await journalEntriesAPI.getByClient(activeAccountingClientId, params);
            const payload = response?.data;
            const entries = Array.isArray(response)
                ? response
                : Array.isArray(payload?.entries)
                    ? payload.entries
                    : Array.isArray(payload)
                        ? payload
                        : [];
            return entries.map((entry) => ({
                ...entry,
                createdBy: entry.createdBy || { name: 'N/A' },
                postedBy: entry.postedBy || null,
            }));
        },
    });
    const loading = journalEntriesQuery.isFetching;

    useEffect(() => {
        if (!isAuthReady) return;
        if (!isBillingTenant) {
            loadAccountingClients();
        }
    }, [isAuthReady]);

    useEffect(() => {
        if (!isAuthReady) return;
        if (isBillingTenant) {
            const storeId = localStorage.getItem('accountingClientId')
                || localStorage.getItem('storeId')
                || '';
            if (storeId) setSelectedAccountingClient(storeId);
            setContextLoading(false);
        }
    }, [isAuthReady, isBillingTenant]);

    // Keep local selector in sync with the global header context switcher.
    useEffect(() => {
        if (!isBillingTenant && contextAccountingClientId && contextAccountingClientId !== selectedAccountingClient) {
            setSelectedAccountingClient(contextAccountingClientId);
        }
    }, [contextAccountingClientId, selectedAccountingClient, isBillingTenant]);

    useEffect(() => {
        setAllEntries(journalEntriesQuery.data || []);
    }, [journalEntriesQuery.data]);

    useEffect(() => {
        filterJournalEntries();
    }, [searchTerm, statusFilter, allEntries]);

    // ✅ Load accounting clients from API
    const loadAccountingClients = async () => {
        try {
            setContextLoading(true);
            
            // ✅ Try to use cached prefetch data first (if available)
            let clients = [];
            const cachedData = getCachedRouteData('/journal-entry');
            if (cachedData?.accountingClients && cachedData.accountingClients.length > 0) {
                clients = cachedData.accountingClients;
                // Don't await the fresh fetch - use cache immediately
                setAccountingClients(clients);
                
                // Initialize selected client from context → cache → localStorage
                const storedId = contextAccountingClientId || selectedClient?._id || localStorage.getItem('accountingClientId');
                if (storedId && clients.some((c) => c._id === storedId)) {
                    setSelectedAccountingClient(storedId);
                } else if (clients.length > 0) {
                    setSelectedAccountingClient(clients[0]._id);
                    localStorage.setItem('accountingClientId', clients[0]._id);
                }
                
                setContextLoading(false);
                
                // Fetch fresh data in background (don't wait for it)
                accountingClientsAPI.getActive().then(response => {
                    const freshClients = response.data || response || [];
                    setAccountingClients(freshClients);
                }).catch(() => {
                    // Keep using cached data if fresh fetch fails
                });
                return;
            }
            
            // If no cache, do the normal fetch
            const response = await accountingClientsAPI.getActive();
            clients = response.data || response || [];
            setAccountingClients(clients);
            
            // Determine which client should be selected — prefer context
            const storedId = contextAccountingClientId || selectedClient?._id || localStorage.getItem('accountingClientId');
            if (storedId && clients.some((c) => c._id === storedId)) {
                setSelectedAccountingClient(storedId);
            } else if (clients.length > 0) {
                setSelectedAccountingClient(clients[0]._id);
                localStorage.setItem('accountingClientId', clients[0]._id);
            }
        } catch (err) {
            console.error('Failed to load accounting clients:', err);
            setError('Failed to load accounting clients');
        } finally {
            setContextLoading(false);
        }
    };

    // Note: the global header switcher in <Layout> persists context and
    // emits a same-tab event. We keep this
    // handler around for legacy reasons, but it isn’t currently wired up.
    const handleAccountingClientSelect = (clientId) => {
        setSelectedAccountingClient(clientId);
        localStorage.setItem('accountingClientId', clientId);
    };

    // ADD THIS MISSING FUNCTION
    const handleDeleteEntry = async (entryId) => {
        try {
            await journalEntriesAPI.delete(entryId);
            await queryClient.invalidateQueries({ queryKey: ['journal-entries', activeAccountingClientId] });
            setSuccess('Journal entry deleted successfully');
            
            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to delete journal entry');
        }
    };

    const filterJournalEntries = () => {
        // Date range is applied server-side via the query params.
        let filtered = [...allEntries];

        // Apply search term filter
        if (searchTerm) {
            const lowercasedSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(entry => 
                (entry.entryNumber?.toLowerCase() || '').includes(lowercasedSearch) ||
                (entry.description?.toLowerCase() || '').includes(lowercasedSearch) ||
                (entry.referenceNumber?.toLowerCase() || '').includes(lowercasedSearch) ||
                (formatDate(entry.entryDate)?.includes(lowercasedSearch))
            );
        }

        setFilteredEntries(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const statusBadgeClass = (status) => {
        switch (status) {
            case 'draft':
                return 'bg-ink-canvas text-ink-body';
            case 'posted':
                return 'bg-success-50 text-success-700';
            case 'void':
                return 'bg-danger-50 text-danger-700';
            default:
                return 'bg-ink-canvas text-ink-body';
        }
    };

    const sourceBadgeClass = (source) => {
        switch (source) {
            case 'manual':
                return 'bg-blue-100 text-blue-800';
            case 'sales':
                return 'bg-purple-100 text-purple-800';
            case 'purchase':
                return 'bg-orange-100 text-orange-800';
            case 'payment':
                return 'bg-teal-100 text-teal-800';
            case 'receipt':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const columns = [
        {
            title: 'Entry Number',
            key: 'entryNumber',
            render: (entry) => (
                <span className="font-mono font-medium">{entry.entryNumber}</span>
            )
        },
        {
            title: 'Date',
            key: 'entryDate',
            render: (entry) => formatDate(entry.entryDate)
        },
        {
            title: 'Description',
            key: 'memo',
            render: (entry) => (
                <div>
                    <div className="font-medium max-w-sm text-wrap">{entry.memo}</div>
                    {entry.referenceNumber && (
                        <div className="text-xs text-gray-500 w-2">
                            Ref: {entry.referenceNumber}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Amount',
            key: 'totalDebit',
            render: (entry) => (
                <div className="text-right font-mono text-sm">
                    {formatCurrency(entry.totalDebit) }
                </div>
            )
        },
        {
            title: 'Status',
            key: 'status',
            render: (entry) => (
                <span className={`px-2 py-1 rounded-md text-xs font-medium capitalize ${statusBadgeClass(entry.status)}`}>
                    {entry.status}
                </span>
            )
        },
       /* {
            title: 'Posted By',
            key: 'postedBy',
            render: (entry) => (
                <div className="text-sm text-gray-600">
                    {entry.status === 'posted' 
                        ? (entry.postedBy?.name || 'System')
                        : '-'
                    }
                </div>
            )
        }*/
    ];

    return (
        <Layout 
            pageTitle="Journal Entries"
            contextKind="accounting_client"
            contextOptions={accountingClients}
            activeContextId={selectedAccountingClient}
            onContextSelect={handleAccountingClientSelect}
        >
            <div className="bg-gray-100 min-h-screen p-6">

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                        {success}
                    </div>
                )}

                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-4">
                        <DebouncedSearchInput
                            placeholder="Search by entry number, description, reference..."
                            onSearch={handleSearch}
                            className="flex-1 mr-4 max-w-md"
                        />

                        <div className="flex space-x-2">
                            <input
                                type="date"
                                lang="en-US"
                                value={dateRange.startDate}
                                onChange={(e) => handleDateChange('startDate', e.target.value)}
                                className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                placeholder="From Date"
                            />
                            <input
                                type="date"
                                lang="en-US"
                                value={dateRange.endDate}
                                onChange={(e) => handleDateChange('endDate', e.target.value)}
                                className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                placeholder="To Date"
                            />
                            {/* Drops the date range so users can search a ref no. without knowing its date. */}
                            <Button
                                variant="outline"
                                onClick={() => setDateRange({ startDate: '', endDate: '' })}
                                disabled={!dateRange.startDate && !dateRange.endDate}
                            >
                                Clear
                            </Button>
                        </div>

                        {isKaryuClient && (
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                                <option value="active">Active</option>
                                <option value="all">All</option>
                                <option value="void">Void</option>
                            </select>
                        )}
                    </div>

                    <Button onClick={() => navigate('/journal-entry/new')} variant="primary">
                        + New Entry
                    </Button>
                </div>

                <div className="mb-2">
                    {searchTerm && (
                        <div className="text-sm text-gray-500">
                            Showing {filteredEntries.length} of {allEntries.length} journal entries matching "{searchTerm}"
                        </div>
                    )}
                </div>

                <DataTable
                    columns={columns}
                    data={filteredEntries}
                    loading={loading}
                    emptyMessage="No journal entries found"
                    onRowClick={(entry) => navigate(`/journal-entry/${entry._id}/edit`)}
                    onEdit={(entry) => {
                        if (entry.status === 'draft') {
                            navigate(`/journal-entry/${entry._id}/edit`);
                        }
                    }}
                    onDelete={(entry) => {
                        if (entry.status === 'draft') {
                            if (window.confirm(`Are you sure you want to delete draft entry ${entry.entryNumber}?`)) {
                                handleDeleteEntry(entry._id);
                            }
                        }
                    }}
                    disableEdit={(entry) => entry.status !== 'draft'}
                    disableDelete={(entry) => entry.status !== 'draft'}
                    editTooltip={(entry) => 
                        entry.status !== 'draft' ? 'Only draft entries can be edited' : 'Edit entry'
                    }
                    deleteTooltip={(entry) => 
                        entry.status !== 'draft' ? 'Only draft entries can be deleted' : 'Delete entry'
                    }
                />
            </div>
        </Layout>
    );
};

export default JournalEntriesList;
